import React, { useEffect, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, defs, linearGradient, stop,
} from 'recharts';
import { motion } from 'framer-motion';
import {
  FaBuilding, FaUsers, FaBan, FaCommentAlt,
  FaTicketAlt, FaCheckCircle, FaExclamationTriangle,
  FaHistory,
} from 'react-icons/fa';
import { dashboardService } from '../../services/dashboardService';
import StatCard from '../../components/StatCard';
import Skeleton from '../../components/Skeleton';
import PulseDot from '../../components/PulseDot';
import { staggerContainer, listItem } from '../../components/motion/motionPresets';

/* ── helpers ── */
const fmt = (n) => (n ?? 0).toLocaleString();
const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const fmtShortDate = (s) => {
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
};

const ACTION_LABELS = {
  block_user:    'Bloqueó usuario',
  unblock_user:  'Desbloqueó usuario',
  update_plan:   'Cambió plan',
  reset_usage:   'Reseteó uso',
  update_limits: 'Actualizó límites',
  create_user:   'Creó usuario',
  delete_user:   'Eliminó usuario',
};

const SectionTitle = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#4A5270' }}>
    {children}
  </p>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 text-xs" style={{ backgroundColor: '#1a1b30', border: '1px solid rgba(255,255,255,0.08)' }}>
      <p className="mb-1 font-semibold" style={{ color: '#9BA3B8' }}>{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: <strong>{fmt(p.value)}</strong>
        </p>
      ))}
    </div>
  );
};

const cardStyle = {
  background: 'linear-gradient(160deg, #161731 0%, #12132a 55%, #0f1024 100%)',
  border: '1px solid rgba(255,255,255,0.07)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
  borderRadius: '12px',
  padding: '20px',
  position: 'relative',
  overflow: 'hidden',
};

/* ── main ── */
const DashboardPage = () => {
  const [stats, setStats]     = useState(null);
  const [chart, setChart]     = useState([]);
  const [tickets, setTickets] = useState({ open: 0, closed: 0, inProgress: 0 });
  const [log, setLog]         = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, c, t, l] = await Promise.all([
          dashboardService.getStats(),
          dashboardService.getUsageLast30Days(),
          dashboardService.getTicketStats(),
          dashboardService.getAdminLog(),
        ]);
        setStats(s);
        setChart(c);
        setTickets(t);
        setLog(l);
      } catch (e) {
        console.error('Dashboard load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-4 gap-4">
          {[0,1,2,3].map(i => <Skeleton.StatCard key={i} />)}
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Skeleton.Card height="280px" className="col-span-2" />
          <Skeleton.Card height="280px" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Skeleton.Card height="200px" />
          <Skeleton.Card height="200px" className="col-span-2" />
        </div>
      </div>
    );
  }

  const totalMsgToday = (stats?.msgToday?.text ?? 0) + (stats?.msgToday?.voice ?? 0);

  return (
    <div className="space-y-6">

      {/* ── Tarjetas resumen ── */}
      <motion.div
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        <StatCard icon={FaBuilding} label="Proyectos activos" value={stats?.activeProjects} subText={`${stats?.inactiveProjects ?? 0} inactivos`} color="#3B82F6" delay={0} accent />
        <StatCard icon={FaUsers} label="Clientes registrados" value={stats?.totalClients} color="#22C55E" delay={0.06} />
        <StatCard icon={FaBan} label="Clientes bloqueados" value={stats?.blockedCount} color={stats?.blockedCount > 0 ? '#F87171' : '#4A5270'} delay={0.12} />
        <StatCard icon={FaCommentAlt} label="Mensajes hoy" value={totalMsgToday} subText={`${fmt(stats?.msgToday?.text)} texto · ${fmt(stats?.msgToday?.voice)} voz`} color="#A78BFA" delay={0.18} />
      </motion.div>

      {/* ── Fila central: gráfica + tickets ── */}
      <div className="grid grid-cols-3 gap-4">

        {/* Gráfica 30 días */}
        <motion.div
          className="col-span-2"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.16,1,0.3,1], delay: 0.1 }}
          style={cardStyle}
        >
          {/* accent line */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, #DC1F26 0%, #3B82F6 60%, #00C2FF 100%)' }} />
          <SectionTitle>Mensajes — últimos 30 días</SectionTitle>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chart} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradText" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.30} />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradVoice" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00C2FF" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#00C2FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="date" tickFormatter={fmtShortDate} tick={{ fill: '#3E4460', fontSize: 10 }} axisLine={false} tickLine={false} interval={4} />
              <YAxis tick={{ fill: '#3E4460', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#4A5270', paddingTop: 8 }} />
              <Area type="monotone" dataKey="text" name="Texto" stroke="#3B82F6" strokeWidth={2} fill="url(#gradText)" dot={false} activeDot={{ r: 4 }} animationDuration={1200} animationEasing="ease-out" />
              <Area type="monotone" dataKey="voice" name="Voz" stroke="#00C2FF" strokeWidth={2} fill="url(#gradVoice)" dot={false} activeDot={{ r: 4 }} animationDuration={1400} animationEasing="ease-out" />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Tickets */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.16,1,0.3,1], delay: 0.18 }}
          style={{ ...cardStyle, display: 'flex', flexDirection: 'column' }}
        >
          <SectionTitle>Soporte — tickets</SectionTitle>
          <div className="flex flex-col gap-3 flex-1 justify-center">
            <TicketRow icon={FaExclamationTriangle} label="Abiertos"    value={tickets.open}        color="#F87171" showPulse={tickets.open > 0} />
            <TicketRow icon={FaTicketAlt}           label="En progreso" value={tickets.inProgress}  color="#FBBF24" />
            <TicketRow icon={FaCheckCircle}         label="Cerrados"    value={tickets.closed}      color="#4ADE80" />
          </div>
          <div className="mt-4 pt-3 text-xs text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.05)', color: '#2A2E45' }}>
            Total: {fmt(tickets.open + tickets.inProgress + tickets.closed)}
          </div>
        </motion.div>
      </div>

      {/* ── Fila inferior: alertas + log ── */}
      <div className="grid grid-cols-3 gap-4">

        {/* Alertas */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.16,1,0.3,1], delay: 0.24 }}
          style={cardStyle}
        >
          <SectionTitle>Usuarios sobre límite</SectionTitle>
          {stats?.overLimit?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2">
              <FaCheckCircle size={22} style={{ color: '#22C55E' }} />
              <p className="text-xs" style={{ color: '#3E4460' }}>Todos dentro del límite</p>
            </div>
          ) : (
            <motion.div className="space-y-2 max-h-48 overflow-y-auto pr-1" variants={staggerContainer} initial="hidden" animate="visible">
              {stats.overLimit.map(u => (
                <motion.div key={u.user_id} variants={listItem} className="flex items-center justify-between py-2 px-3 rounded-lg"
                  style={{ backgroundColor: 'rgba(248,113,113,0.07)', border: '1px solid rgba(248,113,113,0.15)' }}>
                  <div>
                    <p className="text-xs font-medium" style={{ color: '#E8EAF0' }}>{u.email || u.user_id?.slice(0, 8)}</p>
                    <p className="text-[10px]" style={{ color: '#F87171' }}>
                      Texto: {fmt(u.text_used)}/{fmt(u.text_limit)} · Voz: {fmt(u.voice_used)}/{fmt(u.voice_limit)}
                    </p>
                  </div>
                  <motion.span animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                    <FaExclamationTriangle size={11} style={{ color: '#F87171', flexShrink: 0 }} />
                  </motion.span>
                </motion.div>
              ))}
            </motion.div>
          )}
        </motion.div>

        {/* Admin log */}
        <motion.div
          className="col-span-2"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.16,1,0.3,1], delay: 0.3 }}
          style={cardStyle}
        >
          <SectionTitle>Actividad reciente</SectionTitle>
          {log.length === 0 ? (
            <p className="text-xs" style={{ color: '#3E4460' }}>Sin actividad registrada.</p>
          ) : (
            <motion.div className="space-y-2 max-h-52 overflow-y-auto pr-1" variants={staggerContainer} initial="hidden" animate="visible">
              {log.map(entry => (
                <motion.div key={entry.id} variants={listItem} className="flex items-start gap-3 py-2 px-3 rounded-lg"
                  style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                  <FaHistory size={11} style={{ color: '#3B82F6', marginTop: 2, flexShrink: 0 }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs" style={{ color: '#C8CDD9' }}>
                      <span style={{ color: '#E8EAF0', fontWeight: 500 }}>{entry.performed_by_email || 'Admin'}</span>
                      {' '}{ACTION_LABELS[entry.action] || entry.action}{' '}
                      {entry.target_email && <span style={{ color: '#6B7391' }}>→ {entry.target_email}</span>}
                    </p>
                  </div>
                  <span className="text-[10px] flex-shrink-0" style={{ color: '#2A2E45' }}>{fmtDate(entry.created_at)}</span>
                </motion.div>
              ))}
            </motion.div>
          )}
        </motion.div>
      </div>

    </div>
  );
};

const TicketRow = ({ icon: Icon, label, value, color, showPulse }) => (
  <motion.div
    className="flex items-center justify-between px-3 py-3 rounded-lg"
    style={{ backgroundColor: color + '11', border: `1px solid ${color}22` }}
    whileHover={{ scale: 1.02 }}
    transition={{ duration: 0.15 }}
  >
    <div className="flex items-center gap-2.5">
      {showPulse ? <PulseDot variant="red" size={8} /> : <Icon size={13} style={{ color }} />}
      <span className="text-sm" style={{ color: '#9BA3B8' }}>{label}</span>
    </div>
    <span className="text-lg font-bold" style={{ color }}>{value}</span>
  </motion.div>
);

export default DashboardPage;
